import React, {
    forwardRef,
    useState,
    useCallback,
    useMemo,
    useRef,
} from 'react';
import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';
import _noop from 'lodash/noop';

import { IconWrapper } from '@mlb/react-patterns/components/Icon/icon.style.tsx';

import { Tracking } from 'bdvp-tracking';

import {
    getMediaPath,
    targetHasDataClickHandler,
    DateUtils,
} from '../../utils';

import { CardType } from '../../utils/constants';

import {
    useInAppView,
    useBasePath,
    useDictionary,
    useLang,
    useMediaConfig,
} from '../../hooks/config';

import ContentCard from '../ContentCard';
import HeroCard from '../HeroCard';
import DeleteReel from '../../containers/DeleteReelButton';
import CommonReelButton from '../../containers/ReelButton';
import ShareButton from '../../components/ShareButton';

import OptionsMenu from './OptionsMenu';
import PopoverContent from './PopoverContent';
import useFocusTrap from './useFocusTrap';

export const cardContexts = {
    REEL_PAGE: 'reelPage',
    MEDIA: 'media',
};

const ToggleInReel = styled(CommonReelButton)`
    ${IconWrapper} {
        padding-top: 0;
        stroke: none;
    }
`;
const optionButtonProps = {
    styleName: 'default',
    shape: 'solid',
    size: 'lg',
    iconSize: 24,
    dataAttr: {
        'data-click-handler': true,
    },
};

const { CONTENT, HERO } = CardType;

const Card = forwardRef(({
    mediaPlayback,
    orientation,
    onClick,
    isLoading,
    isActive,
    cardContext,
    stat,
    searchParams,
    imgSizes,
    preventOverflow,
    preventOverflowPadding,
    popoverPlacement,
    className,
    type,
}, ref) => {

    const basePath = useBasePath();
    const { errorImageSrc = '' } = useMediaConfig();
    const dictionary = useDictionary();
    const {
        clip_menu_text = 'Video detail menu',
    } = dictionary;
    const lang = useLang();
    const [showOptionMenu = true] = useInAppView('optionsMenu');
    const [
        isPopoverOpen,
        setIsPopoverOpen,
    ] = useState(false);
    const theme = useTheme();

    const lightTheme = useMemo(() => ({
        ...theme,
        dark: false,
    }),[theme]);

    const onPopoverOpen = useCallback(() => setIsPopoverOpen(true), [setIsPopoverOpen]);
    const onPopoverClose = useCallback(() => setIsPopoverOpen(false), [setIsPopoverOpen]);

    const optionsMenuRef = useRef();
    const popoverContentRef = useRef();

    useFocusTrap({
        popoverContentRef,
        optionsMenuRef,
        isPopoverOpen,
    });

    const {
        blurb,
        date = '',
        id = '',
        slug = '',
        isInReel: isInReelValue = false,
        feeds = [],
        canAddToReel = true,
    } = mediaPlayback;

    const formattedDate = DateUtils.formatDate(date, lang);
    const isInReel = showOptionMenu && isInReelValue;
    const hasNoFeeds = feeds.length === 0;
    const playbacks = feeds[0]?.playbacks;
    const mediaPath = getMediaPath(mediaPlayback);

    const disabled = hasNoFeeds || !slug || (playbacks && playbacks.length === 0);
    let duration = feeds[0]?.duration ?? '';
    const durationDisabledLabel = 'Not Available';

    if (disabled) {

        duration = durationDisabledLabel;

    }

    const videoDuration = !disabled && duration && DateUtils.formatTime(duration) || '';

    let statInfo;

    if(mediaPlayback.playInfo && stat) {

        statInfo = {
            label: dictionary[stat.dictionaryKey] || stat.defaultLabel,
            value: mediaPlayback.playInfo[stat.dataProperty] || '-',
        };

    }

    const onClickHandler = useCallback((event) => {

        if(targetHasDataClickHandler(event)) {

            return;

        }

        onClick(event);

    }, [onClick]);

    const Share = (
        <ShareButton
            theme={lightTheme}
            mediaId={id}
            labelKey={'share_text'}
            onClick={() => {

                Tracking.trackClipMenuShareButtonClick(slug);

            }}
            {...optionButtonProps}
        />
    );

    const optionsMenuContent = cardContext === cardContexts.REEL_PAGE
        ? (
            <>
                {Share}
                <DeleteReel
                    theme={lightTheme}
                    mediaId={id}
                    {...optionButtonProps}
                />
            </>

        )
        : (
            <>
                <ToggleInReel
                    theme={lightTheme}
                    mediaPlayback={mediaPlayback}
                    iconName={isInReel ? 'delete-reel' : 'add-reel'}
                    disabled={!canAddToReel}
                    onClick={
                        isInReel
                            ? Tracking.trackClipMenuRemoveFromReelButtonClick
                            : Tracking.trackClipMenuAddToReelButtonClick
                    }
                    {...optionButtonProps}
                />
                {Share}
            </>
        );

    let optionsComponent;
    if(showOptionMenu) {

        optionsComponent = (
            <OptionsMenu
                orientation={orientation}
                onPopoverOpen={onPopoverOpen}
                onPopoverClose={onPopoverClose}
                disabled={disabled}
                altText={clip_menu_text}
                optionsMenuContent={<PopoverContent ref={popoverContentRef}>{optionsMenuContent}</PopoverContent>}
                preventOverflow={preventOverflow}
                preventOverflowPadding={preventOverflowPadding}
                popoverPlacement={popoverPlacement}
                popoverOffset={[
                    cardContext === cardContexts.REEL_PAGE ? 40 : 65,
                    -21,
                ]}
                ref={optionsMenuRef}
            />
        );

    };

    const image = feeds[0]?.image ?? {};
    const imageCuts = image.cuts ?? [];
    const imageAltText = image.altText;

    const Component = type === HERO ? HeroCard: ContentCard;

    return (
        <Component
            as={'a'}
            disabled={disabled || isActive || isPopoverOpen}
            title={blurb}
            date={formattedDate}
            duration={videoDuration}
            imageCuts={imageCuts}
            imageSizes={imgSizes}
            errorImageSrc={errorImageSrc}
            imageAltText={imageAltText}
            loading={isLoading}
            optionsComponent={optionsComponent}
            statInfo={statInfo}
            onClick={onClickHandler}
            href={`${basePath}/${mediaPath}${searchParams}`}
            isActive={isActive}
            isInReel={isInReel}
            ref={ref}
            className={className}
        />
    );

});

Card.propTypes = {
    mediaPlayback: PropTypes.object,
    orientation: PropTypes.string,
    onClick: PropTypes.func,
    isLoading: PropTypes.bool,
    isActive: PropTypes.bool,
    cardContext: PropTypes.string,
    stat: PropTypes.shape({
        dataProperty: PropTypes.string.isRequired,
        dictionaryKey: PropTypes.string.isRequired,
        defaultLabel: PropTypes.string.isRequired,
    }),
    searchParams: PropTypes.string,
    imgSizes: PropTypes.string,
    preventOverflow: PropTypes.bool,
    popoverPlacement: PropTypes.string,
    className: PropTypes.string,
    type: PropTypes.string,
};

Card.defaultProps = {
    mediaPlayback: {},
    orientation: 'portrait',
    onClick: _noop,
    isLoading: false,
    isActive: false,
    cardContext: cardContexts.MEDIA,
    stat: null,
    searchParams: '',
    imgSizes: '',
    preventOverflow: true,
    preventOverflowPadding: 16,
    popoverPlacement: 'left',
    className: undefined,
    type: CONTENT,
};

export default Card;
